import { IS_EMBEDDED, tellParent } from './embed';

const AUTH_BASE = 'https://datenputzer.de/api/auth';
const POST_LOGOUT_KEY = 'tresor_post_logout';
const COOKIE_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
let pendingSsoToken = null;
let lastCookieRefreshAt = 0;

export function markTresorPostLogout() {
  try {
    sessionStorage.setItem(POST_LOGOUT_KEY, '1');
  } catch (_) { /* ignore */ }
}

export function clearTresorPostLogout() {
  try {
    sessionStorage.removeItem(POST_LOGOUT_KEY);
  } catch (_) { /* ignore */ }
}

export function isTresorPostLogout() {
  try {
    return sessionStorage.getItem(POST_LOGOUT_KEY) === '1';
  } catch {
    return false;
  }
}

function parseJwtPayload(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    return JSON.parse(atob(padded + pad));
  } catch {
    return null;
  }
}

function clearLegacyToken() {
  try {
    localStorage.removeItem('dp_token');
  } catch (_) { /* ignore */ }
}

export function setPendingSsoToken(token) {
  pendingSsoToken = (token || '').trim() || null;
  clearLegacyToken();
}

/**
 * Access is cookie-only. The refresh endpoint rotates the HttpOnly access cookie.
 */
async function refreshAccessTokenFromCookies() {
  try {
    const refreshRes = await fetch(`${AUTH_BASE}/refresh`, {
      method: 'POST',
      credentials: 'include'
    });
    if (refreshRes.ok) {
      lastCookieRefreshAt = Date.now();
      return true;
    }
  } catch (_) { /* ignore */ }
  return false;
}

export async function ensureAccessToken() {
  if (isTresorPostLogout() && !pendingSsoToken && !window.location.hash.startsWith('#token=')) {
    clearLegacyToken();
    return false;
  }

  clearLegacyToken();
  const token = pendingSsoToken;
  const payload = token ? parseJwtPayload(token) : null;
  const now = Math.floor(Date.now() / 1000);
  const isExpired = payload?.exp != null && payload.exp <= now;
  const isSso = payload?.scope === 'sso';

  // Fresh SSO redirect — exchange while still valid (do not use raw SSO token for API calls)
  if (isSso && !isExpired) {
    try {
      const exRes = await fetch(`${AUTH_BASE}/exchange-sso`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (exRes.ok) {
        pendingSsoToken = null;
        lastCookieRefreshAt = Date.now();
        clearLegacyToken();
        return true;
      }
    } catch (_) { /* ignore */ }
    return false;
  }

  if (isTresorPostLogout()) return false;
  if (Date.now() - lastCookieRefreshAt < COOKIE_REFRESH_INTERVAL_MS) return true;
  return refreshAccessTokenFromCookies();
}

/**
 * End Datenputzer + Tresor session.
 * Default: clear local state + Tresor device cookie, then same-origin logout on datenputzer.de
 * (reliable HttpOnly cookie clearing). Cross-origin /api/auth/logout from the Tresor subdomain
 * is not trusted for cookie deletion in all browsers.
 */
export async function logoutSession({ viaDatenputzerRedirect = true } = {}) {
  // Eingebettet meldet das Dashboard ab, als Ganzes. Sonst bliebe es angemeldet stehen.
  if (IS_EMBEDDED) {
    tellParent('logout');
    return;
  }
  markTresorPostLogout();
  pendingSsoToken = null;
  lastCookieRefreshAt = 0;
  clearLegacyToken();

  try {
    await fetch('/api/tresor/logout', {
      method: 'POST',
      credentials: 'include'
    });
  } catch (_) { /* ignore */ }

  if (viaDatenputzerRedirect) {
    window.location.replace('https://datenputzer.de/tresor-logout.html');
    return;
  }

  try {
    await fetch(`${AUTH_BASE}/logout`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (_) { /* ignore */ }
}
